import { createAppBackendAdapter } from "./appBackendAdapter.js";

function assertApiBaseUrl(value) {
  if (!value || typeof value !== "string") {
    throw new Error("vite_api_base_url is not set");
  }
  return value.replace(/\/$/, "");
}

async function readJsonResponse(response) {
  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }
  if (!response.ok) {
    const message =
      (payload && payload.message) || `request_failed_${response.status}`;
    const error = new Error(message);
    error.status = response.status;
    error.payload = payload;
    throw error;
  }
  return payload;
}

export function createAppClient({ apiBaseUrl, getAuthToken }) {
  const baseUrl = assertApiBaseUrl(apiBaseUrl);
  const getAuthTokenFn =
    typeof getAuthToken === "function" ? getAuthToken : null;
  const backendAdapter = createAppBackendAdapter({
    apiBaseUrl: baseUrl,
    getAuthToken: getAuthTokenFn,
    credentials: "include",
  });

  async function applyAuthHeaders(initHeaders = null) {
    const headers = new Headers(initHeaders || {});
    if (getAuthTokenFn) {
      const token = await getAuthTokenFn();
      if (token && !headers.has("authorization")) {
        headers.set("authorization", `Bearer ${token}`);
      }
    }
    return headers;
  }

  async function fetchSession() {
    const headers = await applyAuthHeaders();
    const response = await fetch(`${baseUrl}/v1/app/session`, {
      credentials: "include",
      headers,
    });
    return readJsonResponse(response);
  }

  async function listJobs(options = {}) {
    const params = new URLSearchParams();
    if (
      typeof options.limit === "number" &&
      Number.isFinite(options.limit) &&
      options.limit > 0
    ) {
      params.set("limit", String(Math.floor(options.limit)));
    }
    if (options.cursor) {
      params.set("cursor", options.cursor);
    }
    const query = params.toString();
    const headers = await applyAuthHeaders();
    const response = await fetch(
      `${baseUrl}/v1/app/transcription_jobs${query ? `?${query}` : ""}`,
      {
        credentials: "include",
        headers,
      },
    );
    return readJsonResponse(response);
  }

  return {
    fetchSession,
    listJobs,
    getBackendAdapter() {
      return backendAdapter;
    },
  };
}
